// app/frontend/pages/Admin/Users/Edit.tsx
import { useState } from "react";
import {
  Box,
  Stack,
  Group,
  Paper,
  Avatar,
  Badge,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconArrowLeft, IconDeviceFloppy, IconShieldCheck, IconUser } from "@tabler/icons-react";
import { Link, router } from "@inertiajs/react";
import { L2KH2, L2KH5, L2KText, L2KCaption } from "../../../components/ui/Typography";
import { L2KInput, L2KSelect, L2KButton } from "../../../components/ui/Forms";
import { l2kNotify } from "../../../components/ui/Feedback";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { SEO } from "../../../components/common/SEO";

interface User {
  id: number;
  name: string;
  email: string;
  admin: boolean;
  subscription_status: string;
  last_sign_in_at: string | null;
  sign_in_count: number;
  current_sign_in_ip: string | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  user: User;
  errors?: string[];
}

export default function UsersEdit({ user }: Props) {
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: {
      name: user.name || "",
      email: user.email || "",
      subscription_status: user.subscription_status || "",
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name must be at least 2 characters" : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : "Invalid email"),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setSaving(true);

    try {
      const response = await fetch(`/admin/users/${user.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
        body: JSON.stringify({ user: values }),
      });

      const data = await response.json();

      if (data.success) {
        l2kNotify.success("Saved", "User updated successfully");
        router.visit("/admin/users");
      } else {
        l2kNotify.error("Error", data.errors?.join(", ") || "Failed to update user");
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active": return "green";
      case "trialing": return "blue";
      case "canceled": return "red";
      case "past_due": return "orange";
      default: return "gray";
    }
  };

  return (
    <>
      <SEO title={`Edit: ${user.name || user.email} - Admin - Learntokiz`} />
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <Link href="/admin/users">
              <L2KButton variant="ghost" leftSection={<IconArrowLeft size={16} />}>
                Back
              </L2KButton>
            </Link>
            <Box>
              <L2KH2 mb={0}>Edit User</L2KH2>
            </Box>
          </Group>
          <L2KButton
            leftSection={<IconDeviceFloppy size={16} />}
            loading={saving}
            onClick={() => form.onSubmit(handleSubmit)()}
          >
            Save Changes
          </L2KButton>
        </Group>

        <Group align="flex-start" wrap="wrap">
          {/* Form */}
          <Paper
            p="lg"
            radius="md"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              flex: 1,
              minWidth: 300,
              maxWidth: 500,
            }}
          >
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <L2KInput
                  label="Name"
                  placeholder="User's name"
                  {...form.getInputProps("name")}
                />

                <L2KInput
                  label="Email"
                  placeholder="user@example.com"
                  {...form.getInputProps("email")}
                />

                <L2KSelect
                  label="Subscription Status"
                  placeholder="Select status"
                  data={[
                    { value: "active", label: "Active" },
                    { value: "trialing", label: "Trialing" },
                    { value: "canceled", label: "Canceled" },
                    { value: "past_due", label: "Past Due" },
                  ]}
                  {...form.getInputProps("subscription_status")}
                />
              </Stack>
            </form>
          </Paper>

          {/* User Info Sidebar */}
          <Paper
            p="lg"
            radius="md"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              minWidth: 280,
            }}
          >
            <Stack gap="md">
              {/* Avatar & Role */}
              <Group>
                <Avatar
                  size="lg"
                  radius="xl"
                  color="orange"
                  styles={{
                    placeholder: {
                      backgroundColor: "rgba(255, 152, 0, 0.2)",
                      color: "#FF9800",
                    },
                  }}
                >
                  {getInitials(user.name || user.email)}
                </Avatar>
                <Box>
                  <L2KText fw={500}>{user.name || "No name"}</L2KText>
                  {user.admin ? (
                    <Badge color="orange" variant="filled" size="sm" leftSection={<IconShieldCheck size={10} />}>
                      Admin
                    </Badge>
                  ) : (
                    <Badge color="gray" variant="light" size="sm" leftSection={<IconUser size={10} />}>
                      Member
                    </Badge>
                  )}
                </Box>
              </Group>

              {/* Stats */}
              <L2KH5 mb={0}>Account Details</L2KH5>
              
              <Stack gap="xs">
                <Group justify="space-between">
                  <L2KCaption variant="tertiary">User ID</L2KCaption>
                  <L2KCaption>{user.id}</L2KCaption>
                </Group>
                <Group justify="space-between">
                  <L2KCaption variant="tertiary">Subscription</L2KCaption>
                  <Badge color={getStatusColor(user.subscription_status)} size="sm" variant="light">
                    {user.subscription_status || "none"}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <L2KCaption variant="tertiary">Joined</L2KCaption>
                  <L2KCaption>{user.created_at}</L2KCaption>
                </Group>
                <Group justify="space-between">
                  <L2KCaption variant="tertiary">Last Sign In</L2KCaption>
                  <L2KCaption>{user.last_sign_in_at || "Never"}</L2KCaption>
                </Group>
                <Group justify="space-between">
                  <L2KCaption variant="tertiary">Sign In Count</L2KCaption>
                  <L2KCaption>{user.sign_in_count || 0}</L2KCaption>
                </Group>
                {user.current_sign_in_ip && (
                  <Group justify="space-between">
                    <L2KCaption variant="tertiary">Last IP</L2KCaption>
                    <L2KCaption>{user.current_sign_in_ip}</L2KCaption>
                  </Group>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Group>
      </Stack>
    </>
  );
}

UsersEdit.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;
